import type { PrimitiveNode } from "../layout/types.js";
import { applyPatchToVideoNode } from "./applyRuntimePatches.js";
import { collectBehaviorDependencies } from "./compileEvents.js";
import { compileBehavior } from "./compileBehavior.js";
import { evaluateWithRenderCache } from "./renderCache.js";
import {
  applyRuntimeDomTargetFlags,
  runCompiledNodeBehavior,
  withCurrentNodeGeometry,
} from "./applyNodeBehavior.js";
import type {
  CompiledVideoNode,
  CompileNodeContext,
  RenderVideoNode,
  RuntimeContext,
} from "./types.js";
import { compileNodeStyle, transitionsTouchTopLeft } from "./helpers.js";
import { uniqueDependencies } from "./method.js";

export function compileVideoNode(
  node: PrimitiveNode,
  compileContext: CompileNodeContext,
): CompiledVideoNode {
  if (node.kind !== "primitive" || node.primitive !== "video") {
    throw new Error("Node is not a video primitive");
  }

  const baseStyle = compileNodeStyle(node.style, node.transitions);
  const behavior = node.behavior
    ? compileBehavior(
        node.behavior,
        node.id,
        compileContext.catalogDeps,
        undefined,
        compileContext.layoutNodeById,
      )
    : undefined;

  const dependencies = uniqueDependencies(
    behavior ? collectBehaviorDependencies(behavior, compileContext) : [],
  );

  return {
    id: node.id,
    dependencies,

    evaluate(ctx: RuntimeContext): RenderVideoNode | null {
      return evaluateWithRenderCache(ctx, node.id, dependencies, () => {
        let renderNode: RenderVideoNode = {
          id: node.id,
          key: ctx.__nodeInstanceKeyPrefix
            ? `${ctx.__nodeInstanceKeyPrefix}${node.id}`
            : node.id,
          x: node.x,
          y: node.y,
          width: node.width,
          height: node.height,
          zIndex: node.zIndex,
          rotation: node.rotation ?? 0,
          kind: "video",
          src: node.src ?? "",
          muted: node.muted ?? true,
          loop: node.loop ?? true,
          autoPlay: true,
          controls: false,
          style: { ...baseStyle },
          hasTopLeftTransition: transitionsTouchTopLeft(node.transitions),
        };

        if (behavior) {
          const behaviorCtx = withCurrentNodeGeometry(ctx, {
            x: node.x,
            y: node.y,
            width: node.width,
            height: node.height,
            rotation: node.rotation ?? 0,
          });
          const { patches, effects, continuations, runtimeDomTarget } =
            runCompiledNodeBehavior(behavior, behaviorCtx, node.id);

          for (const patch of patches) {
            renderNode = applyPatchToVideoNode(renderNode, patch);
          }

          ctx.__runtimeEffects?.push(...effects);
          ctx.__runtimeContinuations?.push(...continuations);
          renderNode = applyRuntimeDomTargetFlags(
            renderNode,
            runtimeDomTarget,
            behavior,
          );
        }

        if (renderNode.style.visibility === false) {
          return null;
        }

        return renderNode;
      });
    },
  };
}
